// Fragrance tiles for fragrance-choice gift sets (snippets/fragrance-picker.liquid).
// Selection itself is handled by <variant-radios> in product-variant.js; this adds the family
// filter chips, the "what it smells like" line for the chosen fragrance and, on phones, the
// bottom sheet the tiles open in.
if (!customElements.get("fragrance-picker")) {
  class FragrancePicker extends HTMLElement {
    connectedCallback() {
      this.tiles = Array.from(this.querySelectorAll(".fragrance-picker__tile"));
      this.chips = Array.from(this.querySelectorAll(".fragrance-picker__family"));
      this.trigger = this.querySelector(".fragrance-picker__trigger");
      this.sheet = this.querySelector(".fragrance-picker__sheet");
      this.phone = window.matchMedia("(max-width: 749px)");

      this.chips.forEach((chip) =>
        chip.addEventListener("click", () => this.filter(chip))
      );
      this.addEventListener("change", (event) => {
        if (!event.target.matches(".fragrance-picker__input")) return;
        this.showAbout(event.target.value);
        this.updateTrigger(event.target);
        // Choosing a fragrance outside the active family ends that browse: go back to All so the
        // chosen tile isn't left faded.
        const tile = event.target.nextElementSibling;
        if (tile && tile.classList.contains("is-dimmed") && this.chips.length) this.filter(this.chips[0]);
        // A tap or click is a choice: let the tick show, then close the sheet. Arrow keys select as
        // they move through the tiles, so keyboard users close with Enter, Escape or the close button.
        if (this.isOpen() && this.pointerChoice) setTimeout(() => this.close(), 250);
        this.pointerChoice = false;
      });
      this.addEventListener("pointerdown", (event) => {
        const tile = event.target.closest(".fragrance-picker__tile");
        this.pointerChoice = !!tile;
        // Tapping the fragrance that's already chosen fires no change event; close on its click.
        this.pointerOnChecked = !!tile && tile.previousElementSibling?.checked;
      });
      this.addEventListener("click", (event) => {
        if (this.isOpen() && this.pointerOnChecked && event.target.closest(".fragrance-picker__tile")) {
          this.pointerOnChecked = false;
          this.pointerChoice = false;
          setTimeout(() => this.close(), 150);
        }
      });

      if (this.trigger) this.trigger.addEventListener("click", () => this.open());
      this.querySelectorAll("[data-sheet-close]").forEach((el) =>
        el.addEventListener("click", () => this.close())
      );
      this.addEventListener("keydown", (event) => {
        if (!this.isOpen()) return;
        if (event.key === "Escape") this.close();
        // Enter confirms the fragrance. The radios belong to the product form, so without this
        // Enter would submit it and add to the basket.
        if (event.key === "Enter" && event.target.matches(".fragrance-picker__input")) {
          event.preventDefault();
          this.close();
        }
      });
      // Leaving phone width with the sheet open: tidy up, since the tiles are inline again.
      this.phone.addEventListener("change", () => {
        if (!this.phone.matches && this.isOpen()) this.close(false);
      });
    }

    // Fades tiles outside the chosen family; they stay selectable.
    filter(activeChip) {
      const family = activeChip.dataset.family;
      this.chips.forEach((chip) =>
        chip.setAttribute("aria-pressed", chip === activeChip ? "true" : "false")
      );
      this.tiles.forEach((tile) => {
        const inFamily = !family || tile.dataset.families.includes(`|${family}|`);
        tile.classList.toggle("is-dimmed", !inFamily);
      });
    }

    showAbout(value) {
      this.querySelectorAll(".fragrance-picker__about-line").forEach((line) => {
        line.hidden = line.dataset.value !== value;
      });
    }

    // The phone summary shows the chosen fragrance's image and name.
    updateTrigger(input) {
      if (!this.trigger) return;
      const name = this.trigger.querySelector(".fragrance-picker__trigger-name");
      if (name) name.textContent = input.value;
      const media = this.trigger.querySelector(".fragrance-picker__trigger-media");
      const image = input.nextElementSibling?.querySelector(".fragrance-picker__media img");
      if (media) media.innerHTML = image ? image.outerHTML.replace(/ loading="lazy"/, "") : "";
    }

    isOpen() {
      return this.classList.contains("is-open");
    }

    open() {
      if (!this.sheet) return;
      this.classList.add("is-open");
      this.sheet.setAttribute("role", "dialog");
      this.sheet.setAttribute("aria-modal", "true");
      this.trigger.setAttribute("aria-expanded", "true");
      document.body.classList.add("overflow-hidden");
      // Start with the chosen tile in view and focused, so arrow keys move from there.
      const checked = this.querySelector(".fragrance-picker__input:checked");
      const tile = checked?.nextElementSibling;
      if (tile) {
        const offset = tile.getBoundingClientRect().top - this.sheet.getBoundingClientRect().top;
        this.sheet.scrollTop = Math.max(0, this.sheet.scrollTop + offset - this.sheet.clientHeight / 3);
      }
      (checked || this.sheet).focus({ preventScroll: true });
    }

    close(returnFocus = true) {
      if (!this.sheet) return;
      this.classList.remove("is-open");
      this.sheet.removeAttribute("role");
      this.sheet.removeAttribute("aria-modal");
      this.trigger.setAttribute("aria-expanded", "false");
      document.body.classList.remove("overflow-hidden");
      if (returnFocus) this.trigger.focus({ preventScroll: true });
    }
  }

  customElements.define("fragrance-picker", FragrancePicker);
}
